import type { ProjectSection } from '@tomo/core';
import { Avatar, ContextMenu, IconButton, Kbd, Menu, useConfirm, useTheme, type MenuItem, type PaletteColor } from '@tomo/ui';
import {
  ArrowRight,
  ChevronDown,
  ChevronsUpDown,
  Cloud,
  CloudDownload,
  FolderPlus,
  HardDrive,
  LayoutDashboard,
  LogIn,
  LogOut,
  Monitor,
  Moon,
  Palette,
  PanelLeftClose,
  Plus,
  Search,
  Settings,
  Shapes,
  Sparkles,
  Sun,
  Trash2,
  Upload,
  Vote,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { useCloud, signOut } from '../../data/cloud/session.ts';
import {
  useBoards,
  useCollectionTree,
  useMeta,
  usePolls,
  useProject,
  useProjectVersion,
  useReadOnly,
  useSession,
  useSyncState,
} from '../../data/hooks.tsx';
import { useIdentity } from '../../data/identity.ts';
import { useLocalProjects } from '../../data/registry.ts';
import { Icon } from '../../lib/icons.tsx';
import { AuthDialog, type AuthMode } from '../account/AuthDialog.tsx';
import { importProjectFile, loadCloudProjects, openCloudProject, openSampleProject, type WorkspaceProjects } from '../projects/project-actions.ts';
import { newCollectionGroup } from './collection-groups.ts';
import { CollectionList } from './collection-list.tsx';
import { ProjectTile } from './ProjectTile.tsx';
import { addSection, useMissingSections } from './sections.tsx';
import { openNewCollection, openNewProject, openPalette, toggleSidebar, useCollapsedSection } from './ui-state.ts';

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const session = useSession();
  const project = useProject();
  const meta = useMeta();
  const tree = useCollectionTree();
  const boards = useBoards();
  const polls = usePolls();
  const version = useProjectVersion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const counts = useMemo(() => project.counts(), [project, version]);
  const openPolls = polls.filter((p) => p.status === 'open').length;
  const readOnly = useReadOnly();
  const base = `/p/${session.id}`;
  const navigate = useNavigate();
  const confirm = useConfirm();
  const missing = useMissingSections();

  // The one place things are added: a collection from scratch, a group to file them under, or any
  // section the project lacks.
  const addItems: MenuItem[] = [
    { label: 'New collection…', icon: <Plus />, onSelect: () => openNewCollection() },
    { label: 'New group', icon: <FolderPlus />, hint: 'A folder for collections.', onSelect: () => newCollectionGroup(project) },
    ...(missing.length ? [{ type: 'separator' } as MenuItem, { type: 'label', label: 'Add to project' } as MenuItem] : []),
    ...missing.map<MenuItem>((s) => ({
      key: s.id,
      label: s.label,
      hint: s.hint,
      icon: s.icon,
      onSelect: () => {
        addSection(project, s.id);
        navigate(`${base}/${s.id}`);
        onNavigate?.();
      },
    })),
  ];

  /** Boards and Votes go away without taking anything with them, so this isn't a delete. */
  const removeItems = (section: ProjectSection, label: string): MenuItem[] => [
    {
      label: 'Remove from project',
      icon: <Trash2 />,
      danger: true,
      onSelect: async () => {
        const ok = await confirm({
          title: `Remove ${label}?`,
          description: 'Nothing is deleted. Add the section again and everything in it comes back.',
          confirmLabel: 'Remove',
          danger: true,
        });
        if (ok) project.setSection(section, false);
      },
    },
  ];

  return (
    <nav className="sidebar" aria-label="Project navigation">
      <div className="sidebar__top">
        <ProjectSwitcher name={meta.name} icon={meta.icon} color={meta.color} />
        <IconButton label="Collapse sidebar" size="sm" tooltipShortcut="mod+\" className="topbar__collapse" onClick={toggleSidebar}>
          <PanelLeftClose />
        </IconButton>
      </div>

      <button type="button" className="sidebar__search" onClick={() => openPalette()}>
        <Search size={15} />
        <span>Search or jump to…</span>
        <Kbd shortcut="mod+k" size="sm" />
      </button>

      <div className="sidebar__scroll">
        <NavItem to={base} end icon={<LayoutDashboard />} onNavigate={onNavigate}>
          Overview
        </NavItem>
        {meta.sections.includes('boards') && (
          <NavItem
            to={`${base}/boards`}
            icon={<Shapes />}
            iconColor="yellow"
            onNavigate={onNavigate}
            count={boards.length || undefined}
            menu={readOnly ? undefined : removeItems('boards', 'Boards')}
          >
            Boards
          </NavItem>
        )}
        {meta.sections.includes('votes') && (
          <NavItem
            to={`${base}/votes`}
            icon={<Vote />}
            iconColor="purple"
            onNavigate={onNavigate}
            badge={openPolls || undefined}
            menu={readOnly ? undefined : removeItems('votes', 'Votes')}
          >
            Votes
          </NavItem>
        )}

        {/* One list in the user's order: every collection is the same kind of thing, and groups
            are folders over that same list rather than a second sort of place. */}
        <NavSection id="collections" title="Collections" addItems={readOnly ? undefined : addItems} addLabel="Add to project">
          <CollectionList tree={tree} counts={counts} base={base} readOnly={readOnly} onNavigate={onNavigate} />
          {!tree.length && !readOnly && (
            <div className="nav-empty">
              <button type="button" onClick={() => openNewCollection()}>
                Add units, docs, ideas…
              </button>
            </div>
          )}
        </NavSection>
      </div>

      <div className="sidebar__foot">
        <NavItem to={`${base}/trash`} icon={<Trash2 />} onNavigate={onNavigate}>
          Trash
        </NavItem>
        <NavItem to={`${base}/settings`} icon={<Settings />} onNavigate={onNavigate}>
          Project settings
        </NavItem>
        <PlanCard onNavigate={onNavigate} />
        <UserButton />
      </div>
    </nav>
  );
}

/* ---------------------------------------------------------------------------------------------- */

function NavItem({
  to,
  end,
  icon,
  iconColor,
  children,
  count,
  badge,
  menu,
  onNavigate,
}: {
  to: string;
  end?: boolean;
  icon: ReactNode;
  iconColor?: PaletteColor;
  children: ReactNode;
  count?: number;
  badge?: number;
  /** Right-click actions, as collections have. */
  menu?: MenuItem[];
  onNavigate?: () => void;
}) {
  const link = (
    <NavLink to={to} end={end} className="nav-item" onClick={onNavigate}>
      <span className="nav-item__icon" data-color={iconColor}>
        {icon}
      </span>
      <span className="nav-item__label">{children}</span>
      {badge !== undefined && <span className="nav-item__badge">{badge}</span>}
      {count !== undefined && <span className="nav-item__count">{count}</span>}
    </NavLink>
  );
  return menu ? <ContextMenu items={menu}>{link}</ContextMenu> : link;
}

function NavSection({ id, title, children, addItems, addLabel }: { id: string; title: string; children: ReactNode; addItems?: MenuItem[]; addLabel?: string }) {
  const [collapsed, toggle] = useCollapsedSection(id);
  return (
    <div className="nav-section" data-collapsed={collapsed || undefined}>
      <div className="nav-section__head">
        <button type="button" className="nav-section__toggle" onClick={toggle} aria-expanded={!collapsed}>
          {title}
          <ChevronDown />
        </button>
        {addItems && (
          <Menu
            items={addItems}
            trigger={
              <IconButton label={addLabel ?? 'Add'} size="xs" className="nav-section__add">
                <Plus />
              </IconButton>
            }
          />
        )}
      </div>
      {!collapsed && children}
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------- */

/** Where you switch, create and import projects: Tomo has no project list page. */
function ProjectSwitcher({ name, icon, color }: { name: string; icon: string; color: PaletteColor }) {
  const navigate = useNavigate();
  const projects = useLocalProjects();
  const session = useSession();
  const sync = useSyncState();
  const cloud = useCloud();
  const [remote, setRemote] = useState<WorkspaceProjects[]>([]);

  // Cloud projects that aren't on this device yet, fetched fresh whenever the menu opens.
  const loadRemote = () => {
    if (cloud.status === 'signed-in') void loadCloudProjects(cloud.workspaces).then(setRemote);
    else setRemote([]);
  };

  const onDevice = new Set(projects.map((p) => p.id));
  const items: MenuItem[] = [
    { type: 'label', label: 'Projects' },
    ...projects.map<MenuItem>((p) => ({
      key: p.id,
      label: p.name,
      icon: <Icon token={p.icon} />,
      checked: p.id === session.id,
      hint: p.cloud ? <Cloud size={13} /> : undefined,
      onSelect: () => navigate(`/p/${p.id}`),
    })),
    ...remote.flatMap(({ workspace, projects: list }) => {
      const notHere = list.filter((p) => !onDevice.has(p.id));
      if (!notHere.length) return [];
      return [
        { type: 'label', key: `ws-${workspace.id}`, label: `${workspace.name} · cloud` } satisfies MenuItem,
        ...notHere.map<MenuItem>((p) => ({
          key: p.id,
          label: p.name,
          icon: <Icon token={p.icon ?? 'i:gem'} />,
          hint: <CloudDownload size={13} />,
          onSelect: () => void openCloudProject(navigate, p, workspace),
        })),
      ];
    }),
    { type: 'separator' },
    { label: 'New project…', icon: <Plus />, onSelect: openNewProject },
    { label: 'Import .tomo…', icon: <Upload />, onSelect: () => void importProjectFile(navigate) },
    ...(projects.some((p) => p.template === 'sample')
      ? []
      : [{ label: 'Explore the sample', icon: <Sparkles />, onSelect: () => void openSampleProject(navigate) } satisfies MenuItem]),
  ];
  return (
    <Menu
      onOpenChange={(open) => open && loadRemote()}
      trigger={
        <button type="button" className="sidebar__project">
          <ProjectTile icon={icon} color={color} />
          <span className="sidebar__project-name g-truncate">
            {name}
            <span className="sidebar__project-sub">{sync.mode === 'cloud' ? 'Cloud project' : 'On this device'}</span>
          </span>
          <ChevronsUpDown size={14} className="subtle" />
        </button>
      }
      items={items}
      minWidth={240}
    />
  );
}

function PlanCard({ onNavigate }: { onNavigate?: () => void }) {
  const sync = useSyncState();
  const cloud = useCloud();
  const session = useSession();
  const navigate = useNavigate();
  const go = () => {
    onNavigate?.();
    navigate(`/p/${session.id}/settings#cloud`);
  };
  if (sync.mode === 'local') {
    return (
      <button type="button" className="plan-card" onClick={go}>
        <span className="plan-card__row">
          <HardDrive /> Saved on this device
        </span>
        <span>Free forever, works offline.</span>
        {cloud.status !== 'unavailable' && (
          <span className="plan-card__cta">
            Sync &amp; invite your team <ArrowRight size={13} />
          </span>
        )}
      </button>
    );
  }
  const label: Record<string, string> = {
    synced: 'Synced',
    syncing: 'Syncing…',
    connecting: 'Connecting…',
    offline: 'Offline — saved locally',
    'signed-out': 'Sign in to sync',
    'upgrade-required': 'Sync paused — plan',
    forbidden: 'No access',
    error: 'Sync error',
  };
  return (
    <button type="button" className="plan-card" onClick={go}>
      <span className="plan-card__row">
        <span className="sync-dot" data-state={sync.status} /> {label[sync.status] ?? sync.status}
      </span>
      <span>{sync.readOnly ? 'View only' : 'Real-time collaboration on'}</span>
    </button>
  );
}

export function UserButton() {
  const me = useIdentity();
  const cloud = useCloud();
  const navigate = useNavigate();
  const { preference, setPreference } = useTheme();
  const [auth, setAuth] = useState<AuthMode | null>(null);
  const items: MenuItem[] = [
    { type: 'label', label: me.kind === 'account' ? (me.email ?? me.name) : 'Local profile' },
    { label: 'Settings', icon: <Settings />, onSelect: () => navigate('/settings') },
    { type: 'separator' },
    { label: 'Dark', icon: <Moon />, checked: preference === 'dark', keepOpen: true, onSelect: () => setPreference('dark') },
    { label: 'Light', icon: <Sun />, checked: preference === 'light', keepOpen: true, onSelect: () => setPreference('light') },
    { label: 'System', icon: <Monitor />, checked: preference === 'system', keepOpen: true, onSelect: () => setPreference('system') },
    { type: 'separator' },
    { label: 'Design system', icon: <Palette />, onSelect: () => navigate('/design') },
  ];
  if (cloud.status === 'signed-in') items.push({ type: 'separator' }, { label: 'Sign out', icon: <LogOut />, onSelect: () => void signOut() });
  else if (cloud.status === 'signed-out') items.push({ type: 'separator' }, { label: 'Sign in', icon: <LogIn />, onSelect: () => setAuth('signin') });
  return (
    <>
      <Menu
        placement="top-start"
        minWidth={230}
        trigger={
          <button type="button" className="user-button" aria-label="Account menu">
            <Avatar name={me.name} color={me.color} size={28} />
            <span className="user-button__text">
              <span className="user-button__name">{me.name}</span>
              <span className="user-button__sub">{me.kind === 'account' ? me.email : 'Free · this device'}</span>
            </span>
          </button>
        }
        items={items}
      />
      <AuthDialog mode={auth} onClose={() => setAuth(null)} onModeChange={setAuth} />
    </>
  );
}
